import { useTranslation } from 'react-i18next';
import { FiAlertTriangle, FiX } from 'react-icons/fi';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  /** Подпись над блоком с именем элемента */
  itemLabel?: string;
  itemName?: string;
  /** Необязательное поле комментария (например, при отмене заявки) */
  commentField?: {
    label: string;
    placeholder?: string;
    value: string;
    onChange: (value: string) => void;
  };
  confirmText?: string;
  confirmLoadingText?: string;
  isConfirmLoading?: boolean;
  warningText?: string;
  variant?: 'danger' | 'success';
}

export default function DeleteConfirmationWidget({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  itemLabel: itemLabelProp,
  itemName,
  commentField,
  confirmText: confirmTextProp,
  confirmLoadingText: confirmLoadingTextProp,
  isConfirmLoading = false,
  warningText: warningTextProp,
  variant = 'danger',
}: Props) {
  const { t } = useTranslation();
  const itemLabel = itemLabelProp ?? t('deleteWidget.itemLabel');
  const confirmText = confirmTextProp ?? t('deleteWidget.confirmDefault');
  const confirmLoadingText = confirmLoadingTextProp ?? t('deleteWidget.processing');
  const warningText = warningTextProp ?? t('deleteWidget.warningDefault');

  if (!isOpen) return null;

  const isSuccess = variant === 'success';

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[80]">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full mx-4 border border-transparent dark:border-gray-700/60">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700/60">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isSuccess ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-red-100 dark:bg-red-950/40'}`}>
              <FiAlertTriangle className={`w-5 h-5 ${isSuccess ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg dark:hover:bg-white/5 dark:hover:text-gray-200"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
          {itemName && (
            <div className="bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3 mb-4 border dark:border-gray-700/50">
              <p className="text-sm text-gray-500 dark:text-gray-400">{itemLabel}:</p>
              <p className="font-medium text-gray-900 dark:text-gray-100">{itemName}</p>
            </div>
          )}
          {commentField && (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                {commentField.label}
              </label>
              <textarea
                value={commentField.value}
                onChange={(e) => commentField.onChange(e.target.value)}
                placeholder={commentField.placeholder}
                rows={3}
                disabled={isConfirmLoading}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 disabled:opacity-60 dark:border-gray-600 dark:bg-gray-800/80 dark:text-gray-100 dark:placeholder:text-gray-500"
              />
            </div>
          )}
          {warningText && (
            <p className={`text-sm font-medium ${isSuccess ? 'text-emerald-600 dark:text-emerald-300' : 'text-red-600 dark:text-red-300'}`}>
              {warningText}
            </p>
          )}
        </div>
        <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/60 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg dark:text-gray-200 dark:hover:bg-white/5"
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={isConfirmLoading}
            className={`px-4 py-2 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
              isSuccess
                ? 'bg-emerald-600 hover:bg-emerald-700 focus:ring-emerald-500'
                : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
            } ${isConfirmLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
          >
            {isConfirmLoading ? confirmLoadingText : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
